import { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../../db/firebase";

export default function CategoryList() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({
    CategoryName: "",
    CatImgUrl: "",
    ParentCategory: "",
    CategoryType: "main"
  });
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editParentCategory, setEditParentCategory] = useState("");
  const [editCategoryType, setEditCategoryType] = useState("main");
  const [newCategoryImage, setNewCategoryImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const categoryRef = collection(db, "categories");
  const productsRef = collection(db, "products");

  // Function to calculate product count for a category (including subcategories)
  const getProductCountForCategory = async (categoryId) => {
    try {
      const productsQuery = query(
        productsRef,
        where("PCategories", "array-contains", categoryId)
      );
      const productsSnapshot = await getDocs(productsQuery);
      return productsSnapshot.size;
    } catch (error) {
      console.error(`Error getting product count for category ${categoryId}:`, error);
      return 0;
    }
  };

  // Function to calculate product counts for all categories
  const calculateAllProductCounts = async (categoriesData) => {
    const categoriesWithCounts = await Promise.all(
      categoriesData.map(async (category) => {
        const productCount = await getProductCountForCategory(category.id);
        return {
          ...category,
          actualProductCount: productCount,
          staticProductCount: category.ProductCount || 0
        };
      })
    );
    return categoriesWithCounts;
  };

  // Function to organize categories into hierarchy
  const organizeCategoriesHierarchy = (categoriesData) => {
    const mainCategories = categoriesData.filter(cat => !cat.ParentCategory);
    const subCategories = categoriesData.filter(cat => cat.ParentCategory);

    // Add subcategories to their parent categories
    const categoriesWithChildren = mainCategories.map(mainCat => ({
      ...mainCat,
      subcategories: subCategories.filter(subCat => subCat.ParentCategory === mainCat.id)
    }));

    return categoriesWithChildren;
  };

  // Function to get main categories (for dropdowns)
  const getMainCategories = () => {
    return categories.filter(cat => !cat.ParentCategory);
  };

  // Function to get available parent categories (excludes current category and its subcategories)
  const getAvailableParentCategories = (currentCategory = null) => {
    let availableParents = categories.filter(cat => !cat.ParentCategory);
    
    // If editing, exclude current category and its subcategories from parent options
    if (currentCategory) {
      availableParents = availableParents.filter(cat => 
        cat.id !== currentCategory.id && 
        !(currentCategory.subcategories && currentCategory.subcategories.some(sub => sub.id === cat.id))
      );
    }
    
    return availableParents;
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Simplified upload function
  const uploadFile = async (file, folderPath) => {
    if (!file) return null;
    const storageRef = ref(storage, `${folderPath}/${Date.now()}_${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  };

  // Function to delete file from storage
  const deleteStorageFile = async (fileUrl) => {
    if (!fileUrl) return;

    try {
      const matches = fileUrl.match(/o\/(.+?)\?/);
      if (matches && matches[1]) {
        const filePath = decodeURIComponent(matches[1]);
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
        console.log('Deleted category image from storage:', filePath);
      }
    } catch (error) {
      console.error('Error deleting category image:', fileUrl, error);
    }
  };

  const loadCategories = async () => {
    try {
      const snap = await getDocs(categoryRef);
      const categoriesData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Calculate actual product counts
      const categoriesWithActualCounts = await calculateAllProductCounts(categoriesData);

      // Organize into hierarchy for display
      const organizedCategories = organizeCategoriesHierarchy(categoriesWithActualCounts);
      setCategories(organizedCategories);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleAdd = async () => {
    if (!newCategory.CategoryName) return alert("Category name required");

    if (newCategory.CategoryType === "sub" && !newCategory.ParentCategory) {
      return alert("Please select a parent category for the subcategory");
    }

    setLoading(true);
    try {
      let imageUrl = "";

      // Upload image if provided
      if (newCategoryImage) {
        imageUrl = await uploadFile(newCategoryImage, "category_images");
      }

      const categoryData = {
        CategoryName: newCategory.CategoryName,
        CatImgUrl: imageUrl,
        DateCreated: new Date().toISOString(),
        CategoryType: newCategory.CategoryType,
        ...(newCategory.CategoryType === "sub" && { ParentCategory: newCategory.ParentCategory })
      };

      await addDoc(categoryRef, categoryData);

      // Reset form
      setNewCategory({
        CategoryName: "",
        CatImgUrl: "",
        ParentCategory: "",
        CategoryType: "main"
      });
      setNewCategoryImage(null);
      loadCategories();
    } catch (error) {
      console.error("Error adding category:", error);
      alert("Error adding category");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (category) => {
    // Check if category has subcategories
    const hasSubcategories = categories.some(cat =>
      cat.subcategories && cat.subcategories.length > 0 && cat.id === category.id
    );

    const actualProductCount = category.actualProductCount || 0;

    let confirmMessage = `Are you sure you want to delete "${category.CategoryName}"? 
    
This will:
• Delete the category permanently
${category.CatImgUrl ? '• Delete the category image from storage\n' : ''}• Remove this category from ${actualProductCount} product(s)
• Products will keep their other categories`;

    if (hasSubcategories) {
      const subcategoryCount = category.subcategories?.length || 0;
      confirmMessage += `\n• DELETE ${subcategoryCount} SUBCATEGORY${subcategoryCount !== 1 ? 'S' : ''} under this category`;
      confirmMessage += `\n• All products in these subcategories will lose their subcategory assignments`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeletingCategory(category.id);
    try {
      // Delete image from storage if it exists
      if (category.CatImgUrl) {
        await deleteStorageFile(category.CatImgUrl);
      }

      // If it's a main category with subcategories, delete all subcategories first
      if (hasSubcategories && category.subcategories) {
        console.log(`Deleting ${category.subcategories.length} subcategories...`);

        // Delete all subcategories
        const subcategoryDeletions = category.subcategories.map(async (subcategory) => {
          try {
            // Delete subcategory image from storage if it exists
            if (subcategory.CatImgUrl) {
              await deleteStorageFile(subcategory.CatImgUrl);
            }
            // Delete subcategory document
            await deleteDoc(doc(db, "categories", subcategory.id));
            console.log(`Deleted subcategory: ${subcategory.CategoryName}`);
          } catch (subError) {
            console.error(`Error deleting subcategory ${subcategory.CategoryName}:`, subError);
          }
        });

        await Promise.all(subcategoryDeletions);
        console.log(`All subcategories deleted for ${category.CategoryName}`);
      }

      // Delete the main category
      await deleteDoc(doc(db, "categories", category.id));

      let successMessage = `Category "${category.CategoryName}" deleted successfully!`;
      if (hasSubcategories) {
        successMessage += `\n\nAlso deleted ${category.subcategories.length} subcategor${category.subcategories.length !== 1 ? 'ies' : 'y'}.`;
      }

      alert(successMessage);
      loadCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Error deleting category: " + error.message);
    } finally {
      setDeletingCategory(null);
    }
  };

  const handleEdit = async (category) => {
    if (!editName.trim()) return alert("Category name required");

    // NEW: Enhanced validation for category type changes
    if (editCategoryType === "sub" && !editParentCategory) {
      return alert("Please select a parent category for the subcategory");
    }

    // NEW: Check if converting main category with subcategories to subcategory
    if (category.CategoryType === "main" && editCategoryType === "sub" && 
        category.subcategories && category.subcategories.length > 0) {
      if (!window.confirm(
        `WARNING: You are converting a main category with ${category.subcategories.length} subcategor${category.subcategories.length !== 1 ? 'ies' : 'y'} to a subcategory.\n\nThis will:\n• Convert all subcategories to main categories\n• Remove the hierarchy relationship\n\nDo you want to continue?`
      )) {
        return;
      }
    }

    // NEW: Check if converting subcategory to main category
    if (category.CategoryType === "sub" && editCategoryType === "main") {
      if (!window.confirm(
        `You are converting a subcategory to a main category.\n\nThis will:\n• Remove the parent relationship\n• Make this category a top-level category\n\nDo you want to continue?`
      )) {
        return;
      }
    }

    setLoading(true);
    try {
      let imageUrl = category.CatImgUrl;

      // Upload new image if provided
      if (editImage) {
        // Delete old image if it exists
        if (category.CatImgUrl) {
          await deleteStorageFile(category.CatImgUrl);
        }
        // Upload new image
        imageUrl = await uploadFile(editImage, "category_images");
      }

      // NEW: Handle category type conversion
      let updateData = {
        CategoryName: editName,
        CatImgUrl: imageUrl,
        CategoryType: editCategoryType,
      };

      // Handle parent category based on type
      if (editCategoryType === "sub") {
        updateData.ParentCategory = editParentCategory;
      } else {
        updateData.ParentCategory = null;
        
        // If converting from sub to main, also update any subcategories that might reference this
        if (category.CategoryType === "sub") {
          // Find and update any subcategories that had this as parent
          const subcategoriesToUpdate = categories.flatMap(cat => 
            cat.subcategories?.filter(sub => sub.ParentCategory === category.id) || []
          );
          
          if (subcategoriesToUpdate.length > 0) {
            const updatePromises = subcategoriesToUpdate.map(async (subcat) => {
              await updateDoc(doc(db, "categories", subcat.id), {
                ParentCategory: null,
                CategoryType: "main"
              });
            });
            await Promise.all(updatePromises);
          }
        }
      }

      await updateDoc(doc(db, "categories", category.id), updateData);

      // NEW: If converting main with subcategories to subcategory, convert subcategories to main
      if (category.CategoryType === "main" && editCategoryType === "sub" && 
          category.subcategories && category.subcategories.length > 0) {
        const convertPromises = category.subcategories.map(async (subcat) => {
          await updateDoc(doc(db, "categories", subcat.id), {
            ParentCategory: null,
            CategoryType: "main"
          });
        });
        await Promise.all(convertPromises);
      }

      setEditId(null);
      setEditImage(null);
      setEditParentCategory("");
      setEditCategoryType("main");
      loadCategories();
    } catch (error) {
      console.error("Error updating category:", error);
      alert("Error updating category");
    } finally {
      setLoading(false);
    }
  };

  const handleNewImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewCategoryImage(file);
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(file);
    }
  };

  const removeNewImage = () => {
    setNewCategoryImage(null);
  };

  const removeEditImage = () => {
    setEditImage(null);
  };

  const removeExistingImage = async (category) => {
    if (!window.confirm("Are you sure you want to remove this category image?")) {
      return;
    }

    try {
      if (category.CatImgUrl) {
        await deleteStorageFile(category.CatImgUrl);
      }

      await updateDoc(doc(db, "categories", category.id), {
        CatImgUrl: ""
      });

      loadCategories();
    } catch (error) {
      console.error("Error removing category image:", error);
      alert("Error removing category image");
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditImage(null);
    setEditName("");
    setEditParentCategory("");
    setEditCategoryType("main");
  };

  // Function to refresh product counts
  const refreshProductCounts = async () => {
    setLoading(true);
    try {
      await loadCategories();
      alert("Product counts refreshed!");
    } catch (error) {
      console.error("Error refreshing counts:", error);
      alert("Error refreshing product counts");
    } finally {
      setLoading(false);
    }
  };

  // Function to render category row (recursive for subcategories)
  const renderCategoryRow = (category, level = 0) => {
    const isExpanded = expandedCategories.has(category.id);
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const indentStyle = { paddingLeft: `${level * 32}px` };
    const availableParents = getAvailableParentCategories(category);

    return (
      <>
        <tr key={category.id} className="border-b hover:bg-gray-50">
          <td className="p-4" style={indentStyle}>
            {editId === category.id ? (
              <div className="flex flex-col gap-2">
                {editImage ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={URL.createObjectURL(editImage)}
                      alt="New preview"
                      className="w-12 h-12 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={removeEditImage}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ) : category.CatImgUrl ? (
                  <div className="flex flex-col gap-1">
                    <img
                      src={category.CatImgUrl}
                      alt="Current"
                      className="w-12 h-12 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(category)}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
                <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded border border-dashed border-gray-300 text-xs">
                  Change Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEditImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {category.CatImgUrl ? (
                  <div className="flex flex-col gap-1">
                    <img
                      src={category.CatImgUrl}
                      alt={category.CategoryName}
                      className="w-12 h-12 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(category)}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gray-200 rounded border flex items-center justify-center text-gray-400 text-xs">
                    No Image
                  </div>
                )}
              </div>
            )}
          </td>
          <td className="p-4">
            <div className="flex items-center gap-2">
              {hasSubcategories && (
                <button
                  onClick={() => toggleCategoryExpansion(category.id)}
                  className="text-gray-500 hover:text-gray-700 transition-transform"
                >
                  <svg
                    className={`w-4 h-4 transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
              {editId === category.id ? (
                <div className="flex flex-col gap-2 w-full">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="border px-3 py-2 rounded w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Category name"
                  />
                  {/* NEW: Category Type and Parent Selection in Edit Mode */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-gray-600">Type</label>
                      <select
                        value={editCategoryType}
                        onChange={(e) => {
                          setEditCategoryType(e.target.value);
                          if (e.target.value === "main") {
                            setEditParentCategory("");
                          }
                        }}
                        className="border px-2 py-1 rounded w-full text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="main">Main Category</option>
                        <option value="sub">Subcategory</option>
                      </select>
                    </div>
                    {editCategoryType === "sub" && (
                      <div>
                        <label className="block text-xs font-medium mb-1 text-gray-600">Parent</label>
                        <select
                          value={editParentCategory}
                          onChange={(e) => setEditParentCategory(e.target.value)}
                          className="border px-2 py-1 rounded w-full text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">Select Parent</option>
                          {availableParents.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.CategoryName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <span className="font-medium text-gray-900">
                  {category.CategoryName}
                  {level > 0 && (
                    <span className="ml-2 text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                      Subcategory
                    </span>
                  )}
                  {level === 0 && hasSubcategories && (
                    <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                      Main ({category.subcategories.length} sub{category.subcategories.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </span>
              )}
            </div>
          </td>
          <td className="p-4">
            <span
              className={`px-2 py-1 text-xs rounded-full ${(category.actualProductCount || 0) > 0
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
                }`}
              title={`Actual count: ${category.actualProductCount || 0} products`}
            >
              {category.actualProductCount || 0} products
              {category.staticProductCount !== undefined && category.staticProductCount !== category.actualProductCount && (
                <span className="ml-1 text-xs text-gray-500">
                  (was {category.staticProductCount})
                </span>
              )}
            </span>
          </td>
          <td className="p-4 text-gray-600">
            {category.DateCreated ? new Date(category.DateCreated).toLocaleDateString() : 'N/A'}
          </td>
          <td className="p-4">
            <div className="flex gap-2">
              {editId === category.id ? (
                <>
                  <button
                    onClick={() => handleEdit(category)}
                    disabled={loading || !editName.trim() || (editCategoryType === "sub" && !editParentCategory)}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition font-medium disabled:bg-gray-400"
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition font-medium"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setEditId(category.id);
                      setEditName(category.CategoryName);
                      setEditImage(null);
                      setEditParentCategory(category.ParentCategory || "");
                      setEditCategoryType(category.CategoryType || "main");
                    }}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    disabled={deletingCategory === category.id}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition font-medium disabled:bg-red-400 disabled:cursor-not-allowed"
                  >
                    {deletingCategory === category.id ? 'Deleting...' : 'Delete'}
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>

        {/* Render subcategories if expanded */}
        {isExpanded && hasSubcategories && category.subcategories.map(subCategory =>
          renderCategoryRow(subCategory, level + 1)
        )}
      </>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        " "
      </div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-purple-800">Categories</h2>
        <button
          onClick={refreshProductCounts}
          disabled={loading}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition font-medium disabled:bg-gray-400"
        >
          {loading ? "Refreshing..." : "Refresh Counts"}
        </button>
      </div>

      {/* Add New Category */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Add New Category</h3>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category Type *</label>
              <select
                value={newCategory.CategoryType}
                onChange={(e) => setNewCategory({ ...newCategory, CategoryType: e.target.value, ParentCategory: "" })}
                className="border px-3 py-2 rounded w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="main">Main Category</option>
                <option value="sub">Subcategory</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category Name *</label>
              <input
                type="text"
                placeholder="Enter category name"
                value={newCategory.CategoryName}
                onChange={(e) => setNewCategory({ ...newCategory, CategoryName: e.target.value })}
                className="border px-3 py-2 rounded w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {newCategory.CategoryType === "sub" && (
              <div>
                <label className="block text-sm font-medium mb-2">Parent Category *</label>
                <select
                  value={newCategory.ParentCategory}
                  onChange={(e) => setNewCategory({ ...newCategory, ParentCategory: e.target.value })}
                  className="border px-3 py-2 rounded w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Parent Category</option>
                  {getMainCategories().map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.CategoryName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex gap-4 items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Category Image</label>
              <div className="flex gap-2 items-center">
                {newCategoryImage ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={URL.createObjectURL(newCategoryImage)}
                      alt="Preview"
                      className="w-12 h-12 object-cover rounded border"
                    />
                    <span className="text-sm text-gray-600 truncate max-w-32">
                      {newCategoryImage.name}
                    </span>
                    <button
                      type="button"
                      onClick={removeNewImage}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded border border-dashed border-gray-300 text-sm">
                    Choose Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleNewImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleAdd}
            disabled={loading || !newCategory.CategoryName.trim() || (newCategory.CategoryType === "sub" && !newCategory.ParentCategory)}
            className="bg-purple-700 text-white px-6 py-2 rounded hover:bg-purple-800 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed w-fit"
          >
            {loading ? "Adding..." : "Add Category"}
          </button>
        </div>
      </div>

      {/* List Categories */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 font-semibold text-gray-700">Image</th>
              <th className="p-4 font-semibold text-gray-700">Name</th>
              <th className="p-4 font-semibold text-gray-700">Product Count</th>
              <th className="p-4 font-semibold text-gray-700">Date Created</th>
              <th className="p-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => renderCategoryRow(category))}
            {categories.length === 0 && (
              <tr>
                <td colSpan="5" className="p-8 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p className="text-lg font-medium mb-2">No categories found</p>
                    <p className="text-sm">Add your first category above</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}